import { useState } from 'react';

import { Alert, Box, Button, SxProps, Theme } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';

import { supportAccessApi } from '../../axios';
import { useUserDataStore } from '../../newStore/userDataStore';
import { SUPPORT_ACCESS_QUERY_KEY } from '../../queries/supportAccess';

const customStyles = {
  banner: {
    borderRadius: 0,
    alignItems: 'center',
    '& .MuiAlert-message': { flex: 1 },
  },
  text: { fontSize: 14 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * The way back, while a support session is open.
 *
 * It rides above every screen because the person is inside somebody else's
 * account and can navigate anywhere in it; the way out cannot live only on the
 * screen they entered from.
 *
 * Asked of the server rather than kept in the client: the session that decides
 * this is the server's, so a reload, another tab or a browser restart all have to
 * agree with it. Only the support row itself can be in one, so this asks nothing
 * for everybody else.
 */
export function SupportSessionBanner() {
  const currentUser = useUserDataStore((state) => state.currentUser);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const { data, isError } = useQuery({
    queryKey: SUPPORT_ACCESS_QUERY_KEY,
    queryFn: supportAccessApi.getState,
    enabled: Boolean(currentUser?.is_support),
  });

  const leaveMutation = useMutation({
    mutationFn: supportAccessApi.leave,
    onMutate: () => setLeaveError(null),
    // The session belongs to the origin account again, and everything on screen
    // was read as somebody else.
    onSuccess: () => window.location.assign('/'),
    // Without this the one action on the banner failed silently. And there is a
    // case where it fails on purpose: a 403 means the origin user was
    // deactivated while inside, and the server already ended the session, so the
    // only thing left is to land on the login screen.
    onError: (error: { response?: { status?: number; data?: { error?: string } } }) => {
      if (error?.response?.status === 403) {
        window.location.assign('/');
        return;
      }
      setLeaveError(error?.response?.data?.error ?? 'Could not return to your account');
    },
  });

  // `isError` and not just `data.inside`: a 500 left `data` undefined and took
  // the whole notice away, with the way back inside it. The person kept operating
  // a customer's account unaware and with no exit until a reload.
  if (!data?.inside && !isError) return null;

  return (
    <Box data-testid='support-session-banner'>
      <Alert
        severity='warning'
        sx={customStyles.banner}
        action={
          <Button
            color='inherit'
            size='small'
            disabled={leaveMutation.isPending}
            onClick={() => leaveMutation.mutate()}
            data-testid='support-session-return'
          >
            Return to my account
          </Button>
        }
      >
        <Box sx={customStyles.text}>
          {data?.inside ? (
            <>
              You are in the support account of <strong>{data.account_name}</strong>
              {data.origin_account_name ? <> as staff of {data.origin_account_name}</> : null}.
              Every action here is on the record.
            </>
          ) : (
            <>
              We could not confirm whether you are in a support session. If you are, returning is
              still safe.
            </>
          )}
          {/* Alongside the notice and not instead of it: the person is still inside
              somebody else's account, and a failed return does not change that. */}
          {leaveError ? <Box data-testid='support-session-leave-error'>{leaveError}</Box> : null}
        </Box>
      </Alert>
    </Box>
  );
}
